import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { VersionService, SolarData, SolarHistoryPoint, ThermometerReading, DayStat, TempDataPoint, WastePickup, UnifiSnapshot, UnifiDevice, UnifiClient } from './version.service';
import { COMMIT_HASH } from '../version';
import { ColDef, GridOptions } from 'ag-grid-community';

export type LogLevel = 'all' | 'low' | 'medium' | 'high';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  readonly title          = 'Ebersgöns Dashboard';
  readonly frontendCommit = COMMIT_HASH;
  readonly currentYear    = new Date().getFullYear();

  solar:               SolarData | null                          = null;
  thermometers:        ThermometerReading[]                      = [];
  thermometerHistory:  {[deviceId: string]: TempDataPoint[]}     = {};

  solarHistory:          SolarHistoryPoint[]    = [];
  solarChartOpen         = false;

  solarFlash      = false;
  thermoFlash     = false;
  liveDotOn       = true;
  solarUpdatedAt: number | null   = null;
  thermoUpdatedAt: number | null  = null;
  nowTick         = Date.now();   // updated every 5 s to refresh "X ago" labels
  private _liveTimer: any;
  private wakeLock: any = null;
  private readonly onVisibilityChange = async () => {
    if (document.visibilityState === 'visible') await this.requestWakeLock();
  };

  stats:               DayStat[]                                 = [];
  statsMonthFilter     = 'all';
  statsView: 'daily' | 'monthly' = 'daily';
  unifi: UnifiSnapshot | null = null;
  activeTab: 'energie' | 'klima' | 'muell' | 'statistik' | 'netzwerk' = 'energie';
  isMobileView = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize() { this.isMobileView = window.innerWidth < 768; }

  wastePickups:        WastePickup[]                             = [];
  nextPickupsByType:   { type: string; name: string; date: string; icon: string }[] = [];
  logs:                any[]                                     = [];
  logLevel: LogLevel         = 'all';
  logsOpen  = false;
  darkMode  = false;

  statsColDefs:        ColDef[] = [];
  monthlyStatsColDefs: ColDef[] = [];
  colDefs:             ColDef[] = [];

  readonly statsGridOptions: GridOptions = {
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    onFirstDataRendered: (e) => { e.api.autoSizeAllColumns(); },
    onCellDoubleClicked: (e) => {
      const val = e.value != null ? String(e.value) : '';
      navigator.clipboard.writeText(val).catch(() => {});
    },
  };

  readonly gridOptions: GridOptions = {
    animateRows: true,
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    onFirstDataRendered: (e) => {
      e.api.autoSizeColumns(['ts', 'level', 'version', 'className', 'stack']);
    },
    onCellDoubleClicked: (e) => {
      const val = e.value != null ? String(e.value) : '';
      navigator.clipboard.writeText(val).then(() => {
        this.copiedCell = true;
        setTimeout(() => { this.copiedCell = false; }, 1200);
      }).catch(() => {});
    },
  };

  newVersionVisible        = false;
  versionCountdown         = 5;
  private _versionInterval: any = null;
  readonly location   = location;
  copiedCell          = false;
  statsOpen           = false;
  expandedStackRows   = new Set<number>();
  langPickerOpen  = false;
  langSearch      = '';
  changelogOpen   = false;
  changelog: { hash: string; subject: string; date: string; author: string }[] = [];

  readonly languages = [
    { code: 'en', flag: '🇬🇧', name: 'English',    native: 'English' },
    { code: 'de', flag: '🇩🇪', name: 'German',     native: 'Deutsch' },
    { code: 'fr', flag: '🇫🇷', name: 'French',     native: 'Français' },
    { code: 'es', flag: '🇪🇸', name: 'Spanish',    native: 'Español' },
    { code: 'pt', flag: '🇵🇹', name: 'Portuguese', native: 'Português' },
    { code: 'it', flag: '🇮🇹', name: 'Italian',    native: 'Italiano' },
    { code: 'nl', flag: '🇳🇱', name: 'Dutch',      native: 'Nederlands' },
    { code: 'pl', flag: '🇵🇱', name: 'Polish',     native: 'Polski' },
    { code: 'ru', flag: '🇷🇺', name: 'Russian',    native: 'Русский' },
    { code: 'uk', flag: '🇺🇦', name: 'Ukrainian',  native: 'Українська' },
    { code: 'tr', flag: '🇹🇷', name: 'Turkish',    native: 'Türkçe' },
    { code: 'ar', flag: '🇸🇦', name: 'Arabic',     native: 'العربية' },
    { code: 'he', flag: '🇮🇱', name: 'Hebrew',     native: 'עברית' },
    { code: 'fa', flag: '🇮🇷', name: 'Persian',    native: 'فارسی' },
    { code: 'zh', flag: '🇨🇳', name: 'Chinese',    native: '中文' },
    { code: 'ja', flag: '🇯🇵', name: 'Japanese',   native: '日本語' },
    { code: 'ko', flag: '🇰🇷', name: 'Korean',     native: '한국어' },
    { code: 'hi', flag: '🇮🇳', name: 'Hindi',      native: 'हिन्दी' },
    { code: 'bn', flag: '🇧🇩', name: 'Bengali',    native: 'বাংলা' },
    { code: 'ur', flag: '🇵🇰', name: 'Urdu',       native: 'اردو' },
    { code: 'id', flag: '🇮🇩', name: 'Indonesian', native: 'Bahasa Indonesia' },
    { code: 'ms', flag: '🇲🇾', name: 'Malay',      native: 'Bahasa Melayu' },
    { code: 'th', flag: '🇹🇭', name: 'Thai',       native: 'ภาษาไทย' },
    { code: 'vi', flag: '🇻🇳', name: 'Vietnamese', native: 'Tiếng Việt' },
    { code: 'sv', flag: '🇸🇪', name: 'Swedish',    native: 'Svenska' },
    { code: 'no', flag: '🇳🇴', name: 'Norwegian',  native: 'Norsk' },
    { code: 'da', flag: '🇩🇰', name: 'Danish',     native: 'Dansk' },
    { code: 'fi', flag: '🇫🇮', name: 'Finnish',    native: 'Suomi' },
    { code: 'cs', flag: '🇨🇿', name: 'Czech',      native: 'Čeština' },
    { code: 'sk', flag: '🇸🇰', name: 'Slovak',     native: 'Slovenčina' },
    { code: 'hu', flag: '🇭🇺', name: 'Hungarian',  native: 'Magyar' },
    { code: 'ro', flag: '🇷🇴', name: 'Romanian',   native: 'Română' },
    { code: 'bg', flag: '🇧🇬', name: 'Bulgarian',  native: 'Български' },
    { code: 'hr', flag: '🇭🇷', name: 'Croatian',   native: 'Hrvatski' },
    { code: 'sr', flag: '🇷🇸', name: 'Serbian',    native: 'Српски' },
    { code: 'el', flag: '🇬🇷', name: 'Greek',      native: 'Ελληνικά' },
    { code: 'sw', flag: '🇰🇪', name: 'Swahili',    native: 'Kiswahili' },
    { code: 'am', flag: '🇪🇹', name: 'Amharic',    native: 'አማርኛ' },
    { code: 'af', flag: '🇿🇦', name: 'Afrikaans',  native: 'Afrikaans' },
    { code: 'ca', flag: '🇦🇩', name: 'Catalan',    native: 'Català' },
    { code: 'eu', flag: '🏴', name: 'Basque',      native: 'Euskara' },
    { code: 'gl', flag: '🇪🇸', name: 'Galician',   native: 'Galego' },
    { code: 'lt', flag: '🇱🇹', name: 'Lithuanian', native: 'Lietuvių' },
    { code: 'lv', flag: '🇱🇻', name: 'Latvian',    native: 'Latviešu' },
    { code: 'et', flag: '🇪🇪', name: 'Estonian',   native: 'Eesti' },
    { code: 'sl', flag: '🇸🇮', name: 'Slovenian',  native: 'Slovenščina' },
    { code: 'mk', flag: '🇲🇰', name: 'Macedonian', native: 'Македонски' },
    { code: 'sq', flag: '🇦🇱', name: 'Albanian',   native: 'Shqip' },
    { code: 'ka', flag: '🇬🇪', name: 'Georgian',   native: 'ქართული' },
    { code: 'hy', flag: '🇦🇲', name: 'Armenian',   native: 'Հայերեն' },
    { code: 'az', flag: '🇦🇿', name: 'Azerbaijani',native: 'Azərbaycan' },
    { code: 'kk', flag: '🇰🇿', name: 'Kazakh',     native: 'Қазақша' },
    { code: 'uz', flag: '🇺🇿', name: 'Uzbek',      native: 'Oʻzbekcha' },
    { code: 'mn', flag: '🇲🇳', name: 'Mongolian',  native: 'Монгол' },
    { code: 'ne', flag: '🇳🇵', name: 'Nepali',     native: 'नेपाली' },
    { code: 'si', flag: '🇱🇰', name: 'Sinhala',    native: 'සිංහල' },
    { code: 'my', flag: '🇲🇲', name: 'Burmese',    native: 'မြန်မာ' },
    { code: 'km', flag: '🇰🇭', name: 'Khmer',      native: 'ភាសាខ្មែរ' },
    { code: 'lo', flag: '🇱🇦', name: 'Lao',        native: 'ລາວ' },
    { code: 'ta', flag: '🇮🇳', name: 'Tamil',      native: 'தமிழ்' },
    { code: 'te', flag: '🇮🇳', name: 'Telugu',     native: 'తెలుగు' },
    { code: 'ml', flag: '🇮🇳', name: 'Malayalam',  native: 'മലയാളം' },
    { code: 'mr', flag: '🇮🇳', name: 'Marathi',    native: 'मराठी' },
    { code: 'gu', flag: '🇮🇳', name: 'Gujarati',   native: 'ગુજરાતી' },
    { code: 'pa', flag: '🇮🇳', name: 'Punjabi',    native: 'ਪੰਜਾਬੀ' },
    { code: 'ha', flag: '🇳🇬', name: 'Hausa',      native: 'Hausa' },
    { code: 'yo', flag: '🇳🇬', name: 'Yoruba',     native: 'Yorùbá' },
    { code: 'ig', flag: '🇳🇬', name: 'Igbo',       native: 'Igbo' },
    { code: 'zu', flag: '🇿🇦', name: 'Zulu',       native: 'IsiZulu' },
    { code: 'xh', flag: '🇿🇦', name: 'Xhosa',      native: 'IsiXhosa' },
    { code: 'so', flag: '🇸🇴', name: 'Somali',     native: 'Soomaali' },
    { code: 'ht', flag: '🇭🇹', name: 'Haitian Creole', native: 'Kreyòl Ayisyen' },
    { code: 'tl', flag: '🇵🇭', name: 'Filipino',   native: 'Filipino' },
    { code: 'jv', flag: '🇮🇩', name: 'Javanese',   native: 'Basa Jawa' },
    { code: 'cy', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', name: 'Welsh',       native: 'Cymraeg' },
    { code: 'ga', flag: '🇮🇪', name: 'Irish',      native: 'Gaeilge' },
    { code: 'is', flag: '🇮🇸', name: 'Icelandic',  native: 'Íslenska' },
    { code: 'lb', flag: '🇱🇺', name: 'Luxembourgish', native: 'Lëtzebuergesch' },
    { code: 'mt', flag: '🇲🇹', name: 'Maltese',    native: 'Malti' },
  ];

  readonly supportedLangs = new Set([
    'af','am','ar','az','bg','bn','ca','cs','cy','da','de','el','en','es','et',
    'eu','fa','fi','fr','ga','gl','gu','ha','he','hi','hr','ht','hu','hy','id',
    'ig','is','it','ja','jv','ka','kk','km','ko','lb','lo','lt','lv','mk','ml',
    'mn','mr','ms','mt','my','ne','nl','no','pa','pl','pt','ro','ru','si','sk',
    'sl','so','sq','sr','sv','sw','ta','te','th','tl','tr','uk','ur','uz','vi',
    'xh','yo','zh','zu'
  ]);

  get filteredLanguages() {
    const q = this.langSearch.toLowerCase();
    if (!q) return this.languages;
    return this.languages.filter(l =>
      l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q)
    );
  }

  get currentLangFlag(): string {
    return this.languages.find(l => l.code === this.translate.currentLang)?.flag ?? '🌐';
  }

  statsGridHeight(rows: number): number {
    return Math.min(rows * 32 + 38, 600);
  }

  logPassword      = '';
  logAuthError     = false;
  logAuthenticated = false;

  private subs: Subscription[] = [];

  constructor(
    readonly vs: VersionService,
    readonly translate: TranslateService
  ) {
    translate.addLangs(['en', 'de']);
    translate.setDefaultLang('en');

    this.darkMode = localStorage.getItem('darkMode') !== 'false';
    this.applyDarkMode();
  }

  toggleDarkMode(): void {
    this.darkMode = !this.darkMode;
    localStorage.setItem('darkMode', String(this.darkMode));
    this.applyDarkMode();
  }

  private applyDarkMode(): void {
    document.documentElement.setAttribute('data-bs-theme', this.darkMode ? 'dark' : 'light');
  }

  ngOnInit(): void {
    const saved = localStorage.getItem('lang') ?? 'en';
    this.translate.use(saved).subscribe(() => this.buildColDefs());

    // Rebuild column headers when language changes
    this.subs.push(
      this.translate.onLangChange.subscribe(() => this.buildColDefs())
    );

    this._liveTimer = setInterval(() => { this.liveDotOn = !this.liveDotOn; }, 600);
    setInterval(() => { this.nowTick = Date.now(); }, 1000);

    this.vs.connect(this.frontendCommit);

    this.subs.push(
      this.vs.solarHistory$.subscribe(pts => { this.solarHistory = pts; }),
      this.vs.solar$.subscribe(data => {
        this.solar = data;
        this.solarUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 24 * 3600 * 1000;
        const pt: SolarHistoryPoint = { ts: now, pvYieldW: data.pvYieldW, batteryDischargeW: data.batteryDischargeW, gridConsumptionW: data.gridConsumptionW, totalConsumptionW: data.totalConsumptionW, batteryPercent: data.batteryPercent };
        this.solarHistory = [...this.solarHistory.filter(p => p.ts >= cutoff), pt];
        this.solarFlash = false;
        setTimeout(() => { this.solarFlash = true; setTimeout(() => { this.solarFlash = false; }, 900); }, 0);
      }),
      this.vs.thermometers$.subscribe(data => {
        this.thermometers = data;
        this.thermoUpdatedAt = Date.now();
        this.thermoFlash = false;
        setTimeout(() => { this.thermoFlash = true; setTimeout(() => { this.thermoFlash = false; }, 900); }, 0);
      }),
      this.vs.thermometerHistory$.subscribe(h => { this.thermometerHistory = h; }),
      this.vs.stats$.subscribe(data => { this.stats = data; }),
      this.vs.waste$.subscribe(data => {
        this.wastePickups = data;
        this.nextPickupsByType = this._computeNextPickups(data);
      }),
      this.vs.unifi$.subscribe(data => { this.unifi = data; }),
      this.vs.newVersion$.subscribe(() => {
        if (this._versionInterval) return; // already counting down, ignore
        this.newVersionVisible = true;
        this.versionCountdown  = 5;
        this._versionInterval = setInterval(() => {
          this.versionCountdown--;
          if (this.versionCountdown <= 0) { clearInterval(this._versionInterval); location.reload(); }
        }, 1000);
      })
    );
    this.requestWakeLock();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    clearInterval(this._liveTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.wakeLock?.release?.();
  }

  private async requestWakeLock(): Promise<void> {
    if ('wakeLock' in navigator) {
      try { this.wakeLock = await (navigator as any).wakeLock.request('screen'); }
      catch (_) {}
    }
  }

  openChangelog(): void {
    this.changelogOpen = true;
    if (this.changelog.length === 0) {
      fetch('/api/changelog')
        .then(r => r.json())
        .then(data => { this.changelog = data; })
        .catch(() => {});
    }
  }

  openLangPicker(): void {
    this.langPickerOpen = true;
    this.langSearch = '';
  }

  selectLang(code: string): void {
    if (!this.supportedLangs.has(code)) return;
    this.langPickerOpen = false;
    this.switchLang(code);
  }

  switchLang(lang: string): void {
    localStorage.setItem('lang', lang);
    this.translate.use(lang);
  }

  private buildColDefs(): void {
    const t = (key: string) => this.translate.instant(key);

    this.statsColDefs = [
      {
        field: 'date', headerName: t('COLS.TAG'), width: 120, sortable: true, sort: 'desc',
        valueFormatter: p => this.fmtDate(p.value),
      },
      {
        field: 'generationKwh', headerName: t('COLS.ERTRAG'), width: 110, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-warning',
      },
      {
        field: 'consumptionKwh', headerName: t('COLS.VERBRAUCH'), width: 120, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-primary',
      },
      {
        field: 'gridImportKwh', headerName: t('COLS.BEZUG'), width: 100, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-danger',
      },
      {
        field: 'autarkiePercent', headerName: t('COLS.AUTARKIE'), width: 100, sortable: true,
        valueFormatter: p => p.value != null ? p.value.toFixed(0) + ' %' : '–',
        cellClassRules: {
          'text-success fw-bold': p => p.value >= 80,
          'text-warning fw-bold': p => p.value >= 50 && p.value < 80,
          'text-danger fw-bold':  p => p.value < 50,
        },
      },
      {
        field: 'gridExportKwh', headerName: t('COLS.EINSPEISUNG'), width: 120, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-success',
      },
    ];

    this.monthlyStatsColDefs = [
      {
        field: 'date', headerName: t('COLS.MONAT'), width: 130, sortable: true, sort: 'desc',
        valueFormatter: p => {
          if (!p.value) return p.value;
          const d = new Date(p.value + '-01');
          const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
          return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
        },
      },
      {
        field: 'generationKwh', headerName: t('COLS.ERTRAG'), width: 110, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-warning',
      },
      {
        field: 'consumptionKwh', headerName: t('COLS.VERBRAUCH'), width: 120, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-primary',
      },
      {
        field: 'gridImportKwh', headerName: t('COLS.BEZUG'), width: 100, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-danger',
      },
      {
        field: 'autarkiePercent', headerName: t('COLS.AUTARKIE'), width: 100, sortable: true,
        valueFormatter: p => p.value != null ? p.value.toFixed(0) + ' %' : '–',
        cellClassRules: {
          'text-success fw-bold': p => p.value >= 80,
          'text-warning fw-bold': p => p.value >= 50 && p.value < 80,
          'text-danger fw-bold':  p => p.value < 50,
        },
      },
      {
        field: 'gridExportKwh', headerName: t('COLS.EINSPEISUNG'), width: 120, sortable: true,
        valueFormatter: p => this.formatKwh(p.value),
        cellClass: 'text-end text-success',
      },
    ];

    this.colDefs = [
      {
        field: 'ts', headerName: t('COLS.ZEIT'), sortable: true,
        valueFormatter: p => this.fmtTs(p.value),
        sort: 'desc',
      },
      {
        field: 'level', headerName: t('COLS.LEVEL'), sortable: true, filter: true,
        cellRenderer: (p: any) => {
          const colour = this.levelBadge(p.value);
          return `<span class="badge bg-${colour}">${p.value ?? ''}</span>`;
        },
      },
      {
        field: 'version', headerName: t('COLS.VERSION'),
        valueFormatter: p => (p.value ?? '?').slice(0, 7),
      },
      { field: 'className', headerName: t('COLS.KLASSE'), sortable: true, filter: true },
      {
        field: 'message', headerName: t('COLS.NACHRICHT'), flex: 1, filter: true,
        wrapText: true, autoHeight: true,
      },
      {
        field: 'stack', headerName: t('COLS.STACK'), filter: true,
        autoHeight: true,
        cellRenderer: (p: any) => {
          const stack: string = p.value ?? '';
          if (!stack) return '<span class="text-muted">–</span>';
          const idx = p.rowIndex as number;
          const expanded = this.expandedStackRows.has(idx);
          const firstLine = stack.split('\n')[0];
          if (expanded) {
            return `<pre style="font-size:11px;line-height:1.4;margin:0;cursor:pointer;white-space:pre-wrap;word-break:break-all;" title="${this.translate.instant('STACK_EINKLAPPEN')}">▲ ${stack}</pre>`;
          }
          return `<span style="font-family:monospace;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:block;cursor:pointer;" title="${stack.replace(/"/g, '&quot;')}">▶ ${firstLine}</span>`;
        },
        onCellClicked: (p: any) => {
          const idx = p.rowIndex as number;
          if (this.expandedStackRows.has(idx)) {
            this.expandedStackRows.delete(idx);
          } else {
            this.expandedStackRows.add(idx);
          }
          p.api.redrawRows({ rowNodes: [p.node] });
        },
      },
    ];
  }

  fetchLogs(): void {
    const param = this.logLevel === 'all' ? '' : `?level=${this.logLevel}`;
    fetch(`/api/logs${param}`, { headers: { 'X-Log-Password': this.logPassword } })
      .then(r => {
        if (r.status === 401) {
          this.logAuthError     = true;
          this.logAuthenticated = false;
          this.logs = [];
          return null;
        }
        this.logAuthError     = false;
        this.logAuthenticated = true;
        return r.json();
      })
      .then(data => { if (data) this.logs = (data as any[]).reverse(); })
      .catch(() => {});
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.fetchLogs();
  }

  toggleLogs(): void {
    this.logsOpen = !this.logsOpen;
    if (this.logsOpen && this.logAuthenticated) this.fetchLogs();
    if (!this.logsOpen) this.expandedStackRows.clear();
  }

  submitLogPassword(): void {
    this.fetchLogs();
  }

  fmtTs(ts: number | string): string {
    const d = new Date(Number(ts));
    const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
    return d.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'medium' });
  }

  levelBadge(level: string): string {
    return level === 'high' ? 'danger' : level === 'medium' ? 'warning' : 'secondary';
  }

  formatW(w: number): string {
    return Math.abs(w) >= 1000
      ? (w / 1000).toFixed(2) + ' kW'
      : w.toFixed(0) + ' W';
  }

  formatKwh(kwh: number): string {
    return kwh.toFixed(2) + ' kWh';
  }

  fmtDate(dateStr: string): string {
    if (!dateStr || dateStr === '?') return dateStr;
    const d = new Date(dateStr);
    const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
    return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  sensorName(name: string): string {
    const key = `SENSOR_NAMES.${name}`;
    const t = this.translate.instant(key);
    return t === key ? name : t;
  }

  updatedAgo(ts: number | null): string {
    if (ts === null) return '';
    const s = Math.floor((this.nowTick - ts) / 1000);
    if (s < 60)  return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}min`;
    return `${Math.floor(s / 3600)}h`;
  }

  shortHash(hash: string): string {
    if (!hash || hash.length <= 9) return hash || '…';
    return hash.slice(0, 4) + '…' + hash.slice(-4);
  }

  autarkieColor(pct: number): string {
    if (pct >= 80) return 'text-success';
    if (pct >= 50) return 'text-warning';
    return 'text-danger';
  }

  /** Estimated battery runtime (discharging) or time to full (charging), including label. */
  batteryRuntime(solar: SolarData): string {
    if (!solar.batteryCapacityWh) return '';
    if (solar.batteryDischargeW > 0) {
      // Discharging: time until empty
      const hours = (solar.batteryPercent / 100 * solar.batteryCapacityWh) / solar.batteryDischargeW;
      const label = this.translate.instant('AKKU_REST');
      if (hours < 1)  return `${label} ≈ < 1 h`;
      if (hours < 24) return `${label} ≈ ${Math.round(hours)} h`;
      const days = Math.round(hours / 24);
      return `${label} ≈ ${days} ${days === 1 ? this.translate.instant('AKKU_TAG') : this.translate.instant('AKKU_TAGE')}`;
    }
    if (solar.batteryDischargeW < 0) {
      // Charging: time until full
      const chargeW     = -solar.batteryDischargeW;
      const remainingWh = (100 - solar.batteryPercent) / 100 * solar.batteryCapacityWh;
      const hours       = remainingWh / chargeW;
      const label       = this.translate.instant('AKKU_VOLL');
      if (hours < 1/60) return `${label} ≈ < 1 min`;
      if (hours < 1)    return `${label} ≈ ${Math.round(hours * 60)} min`;
      return `${label} ≈ ${Math.round(hours)} h`;
    }
    return '';
  }

  private static readonly WASTE_ICONS: Record<string, string> = {
    RM:  'assets/waste/rm.svg',
    Bio: 'assets/waste/bio.svg',
    GT:  'assets/waste/gt.svg',
    PPK: 'assets/waste/ppk.svg',
    A:   'assets/waste/astwerk.svg',
    SoH: 'assets/waste/soh.svg',
    TB:  'assets/waste/tb.svg',
  };

  private _computeNextPickups(pickups: WastePickup[]): { type: string; name: string; date: string; icon: string }[] {
    const today = new Date().toISOString().slice(0, 10);
    const map   = new Map<string, { type: string; name: string; date: string; icon: string }>();
    for (const p of pickups) {
      if (p.type === 'SoG') continue;
      if (p.date < today)  continue;
      if (!map.has(p.type)) {
        map.set(p.type, {
          type: p.type,
          name: p.name,
          date: p.date,
          icon: AppComponent.WASTE_ICONS[p.type] ?? 'assets/waste/rm.svg',
        });
      }
    }
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  }

  allDatesForType(type: string): string[] {
    const today = new Date().toISOString().slice(0, 10);
    return this.wastePickups
      .filter(p => p.type === type && p.date >= today)
      .map(p => p.date)
      .slice(0, 5);
  }

  daysUntil(dateStr: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr + 'T00:00:00');
    return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  wasteRelative(dateStr: string): string {
    const d = this.daysUntil(dateStr);
    if (d === 0) return this.translate.instant('MUELL_HEUTE');
    if (d === 1) return this.translate.instant('MUELL_MORGEN');
    return this.translate.instant('MUELL_IN_TAGEN', { n: d });
  }

  fmtWasteDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  get todayStat(): DayStat | null {
    const today = new Date().toISOString().slice(0, 10);
    return this.stats.find(s => s.date === today) ?? null;
  }

  get statsMonths(): string[] {
    const months = [...new Set(this.stats.map(s => s.date.slice(0, 7)))];
    return months.sort((a, b) => b.localeCompare(a));
  }

  get filteredStats(): DayStat[] {
    if (this.statsMonthFilter === 'all') return this.stats;
    return this.stats.filter(s => s.date.startsWith(this.statsMonthFilter));
  }

  get monthlyStats(): DayStat[] {
    const map = new Map<string, { gen: number; cons: number; imp: number; exp: number }>();
    for (const s of this.stats) {
      const ym = s.date.slice(0, 7);
      const e  = map.get(ym) ?? { gen: 0, cons: 0, imp: 0, exp: 0 };
      e.gen  += s.generationKwh;
      e.cons += s.consumptionKwh;
      e.imp  += s.gridImportKwh;
      e.exp  += s.gridExportKwh;
      map.set(ym, e);
    }
    return [...map.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([ym, v]) => ({
        date:             ym,
        generationKwh:    v.gen,
        consumptionKwh:   v.cons,
        gridImportKwh:    v.imp,
        gridExportKwh:    v.exp,
        autarkiePercent:  v.cons > 0 ? Math.max(0, (1 - v.imp / v.cons) * 100) : 0,
      } as DayStat));
  }

  fmtMonth(ym: string): string {
    const d = new Date(ym + '-01');
    const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
    return d.toLocaleDateString(locale, { month: 'long' });
  }

  fmtMonthShort(ym: string): string {
    const d = new Date(ym + '-01');
    const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
    return d.toLocaleDateString(locale, { month: 'short' });
  }

  sparklinePoints(deviceId: string): TempDataPoint[] {
    return this.thermometerHistory[deviceId] ?? [];
  }

  sparklineMinMax(deviceId: string): { min: number; max: number } {
    const pts = this.thermometerHistory[deviceId];
    if (!pts?.length) return { min: 0, max: 0 };
    const temps = pts.map(p => p.tempC);
    return { min: Math.min(...temps), max: Math.max(...temps) };
  }

  // ── UniFi helpers ──────────────────────────────────────────────────────────

  fmtBps(bps: number): string {
    if (bps >= 1_000_000) return (bps / 1_000_000).toFixed(1) + ' MB/s';
    if (bps >= 1_000)     return (bps / 1_000).toFixed(0) + ' KB/s';
    return bps.toFixed(0) + ' B/s';
  }

  fmtBytes(bytes: number): string {
    if (bytes >= 1_073_741_824) return (bytes / 1_073_741_824).toFixed(1) + ' GB';
    if (bytes >= 1_048_576)     return (bytes / 1_048_576).toFixed(0) + ' MB';
    if (bytes >= 1_024)         return (bytes / 1_024).toFixed(0) + ' KB';
    return bytes + ' B';
  }

  fmtUptime(seconds: number): string {
    if (seconds <= 0) return '–';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  }

  deviceIcon(type: string): string {
    if (type === 'uap') return '📶';
    if (type === 'usw') return '🔀';
    return '🌐';   // ugw, udm, uxg, unknown
  }

  deviceLabel(type: string): string {
    if (type === 'uap') return 'Access Point';
    if (type === 'usw') return 'Switch';
    if (type === 'ugw') return 'Gateway';
    if (type === 'udm') return 'Dream Machine';
    if (type === 'uxg') return 'Gateway Pro';
    return type;
  }

  signalClass(dBm: number): string {
    if (dBm >= -55) return 'text-success';
    if (dBm >= -70) return 'text-warning';
    return 'text-danger';
  }

  signalLabel(dBm: number): string {
    if (dBm >= -55) return 'stark';
    if (dBm >= -70) return 'mittel';
    return 'schwach';
  }

}
